import { useMemo } from 'react'
import { z } from 'zod'

import {
  requiredSelection,
  requiredTrimmedString,
} from '../../../../../app/_components/form/zodSchemas'
import { useTranslation } from '../../../../../app/_providers/TranslationProvider.client'
import { UseEmbroideryProps } from '../../../../../containers/EmbroideryContainer/EmbroideryProvider'
import { allowedTextInputCharactersPattern } from '../../../../../containers/EmbroideryContainer/utils/unorganized'

export interface Props {
  textMaxChars: number
  textEmbroideryConfig?: UseEmbroideryProps['textEmbroideryConfig']
}

export const useValidationSchema = ({ textMaxChars, textEmbroideryConfig }: Props) => {
  const {
    embroidery: {
      modal: { forms: fixtures },
    },
  } = useTranslation()

  const validationSchema = useMemo(() => {
    const textMaxCharsMessage = fixtures.validationErrorMessages.textMaxCharsTemplate.replace(
      '{{max}}',
      textMaxChars.toString()
    )

    return z.object({
      textColor: requiredSelection(fixtures.validationErrorMessages.requiredNameColor),
      firstLine: requiredTrimmedString(fixtures.validationErrorMessages.requiredFirstLine)
        .max(textMaxChars, textMaxCharsMessage)
        .regex(
          allowedTextInputCharactersPattern,
          fixtures.validationErrorMessages.allowedTextCharacters
        ),
      font: requiredSelection(fixtures.validationErrorMessages.requiredFont),
      secondLine: z
        .string()
        .max(textMaxChars, textMaxCharsMessage)
        .regex(
          allowedTextInputCharactersPattern,
          fixtures.validationErrorMessages.allowedTextCharacters
        )
        .refine(value => {
          if (!value) return true
          if (!textEmbroideryConfig?.secondLineOptions?.length) return true
          // If the value is truthy and we have second line dropdown options, ensure the value matches one of the options.
          return textEmbroideryConfig.secondLineOptions.includes(value)
        }, 'Second line must be a valid selection.')
        .optional(),
      textPlacement: requiredSelection(fixtures.validationErrorMessages.requiredNamePlacement),
    })
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fixtures.validationErrorMessages.allowedTextCharacters,
    fixtures.validationErrorMessages.requiredNameColor,
    fixtures.validationErrorMessages.requiredNamePlacement,
    fixtures.validationErrorMessages.requiredFont,
    fixtures.validationErrorMessages.requiredName,
  ])

  return validationSchema
}
